import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { Helmet } from 'react-helmet';
import { db } from '../firebase';
import PublicMarketingLayout from '../components/layout/PublicMarketingLayout';
import OliveBranch from '../components/marketing/OliveBranch';
import { recordReferralFirstTouch } from '../services/acquisitionService';

interface ReferrerInfo {
  uid: string;
  firstName: string | null;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; referrer: ReferrerInfo | null }
  | { status: 'error'; message: string };

/**
 * Member-to-member referral landing page.
 *
 * URL: `/invite/:code`. Resolves the code to a referrer uid (when one
 * exists), records a referral first-touch in the visitor's cookie, and
 * renders an inviting landing page that pushes through to the standard
 * enrollment flow with the code attached.
 *
 * V1 status:
 *   - The `users/{uid}.referralCode` field doesn't yet exist in the
 *     schema. When a member generates an invite code from inside the
 *     app, that field will get set and this lookup will start returning
 *     a referrer. Until then, the page still works — it just doesn't
 *     name the referrer and `referrerUid` stays null in the cookie.
 *   - Names are only displayed if the referrer has explicitly opted in
 *     to "show my name on my invite page" (see CTO brief §6).
 *
 * See: docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md §4.5 + §6
 */
export default function ReferralLandingPage() {
  const { code } = useParams<{ code: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      setState({ status: 'ready', referrer: null });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        let referrer: ReferrerInfo | null = null;
        try {
          const usersRef = collection(db, 'users');
          const snap = await getDocs(
            query(usersRef, where('referralCode', '==', code), limit(1)),
          );
          if (!snap.empty) {
            const data = snap.docs[0].data() as Record<string, unknown>;
            const showName = data.referralShowName === true;
            referrer = {
              uid: snap.docs[0].id,
              firstName:
                showName && typeof data.firstName === 'string'
                  ? (data.firstName as string)
                  : null,
            };
          }
        } catch (lookupErr) {
          // Lookup failures (rules / index / network) are non-fatal: we
          // still want the visitor to land on a friendly page.
          console.warn('referral code lookup failed', lookupErr);
        }

        if (cancelled) return;
        setState({ status: 'ready', referrer });

        try {
          recordReferralFirstTouch(code, referrer?.uid ?? '');
        } catch (err) {
          console.warn('recordReferralFirstTouch failed', err);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Could not load this invite.';
        setState({ status: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <PublicMarketingLayout>
      <Helmet>
        <title>You've been invited to Melio</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {renderBody(state, code)}
    </PublicMarketingLayout>
  );
}

function renderBody(state: LoadState, code: string | undefined) {
  if (state.status === 'loading') {
    return (
      <section
        className="flex items-center justify-center"
        style={{ backgroundColor: 'var(--gh-bone)', minHeight: '60vh' }}
      >
        <p className="gh-eyebrow-ash">Loading…</p>
      </section>
    );
  }
  if (state.status === 'error') {
    return (
      <section
        className="flex flex-col items-center justify-center px-6 text-center"
        style={{
          backgroundColor: 'var(--gh-bone)',
          minHeight: '60vh',
          paddingTop: '4rem',
          paddingBottom: '4rem',
        }}
      >
        <h1
          className="gh-serif"
          style={{ color: 'var(--gh-graphite)', fontSize: '1.75rem', marginBottom: '1rem' }}
        >
          Something went wrong.
        </h1>
        <p style={{ color: 'var(--gh-ash)', maxWidth: '32rem', marginBottom: '2rem' }}>
          {state.message}
        </p>
        <Link to="/" className="gh-btn-primary" style={{ padding: '12px 24px', fontSize: '0.8125rem' }}>
          Visit the homepage
        </Link>
      </section>
    );
  }
  return <ReferralHero referrer={state.referrer} code={code} />;
}

interface ReferralHeroProps {
  referrer: ReferrerInfo | null;
  code: string | undefined;
}

function ReferralHero({ referrer, code }: ReferralHeroProps) {
  const ctaHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('campaign', 'referral_default');
    if (code) params.set('promo', code);
    return `/enrollment?${params.toString()}`;
  }, [code]);

  const headline = referrer?.firstName
    ? `${referrer.firstName} invited you to Melio.`
    : `You've been invited to Melio.`;

  return (
    <section
      className="px-6 md:px-10"
      style={{ backgroundColor: 'var(--gh-bone)', paddingTop: '5rem', paddingBottom: '6rem' }}
    >
      <div className="mx-auto max-w-3xl text-center">
        <div className="mb-8 flex justify-center">
          <OliveBranch width={88} color="var(--gh-oxblood)" />
        </div>

        <p className="gh-eyebrow-ash mb-6" style={{ letterSpacing: '0.22em' }}>
          A quiet invitation
        </p>

        <h1
          className="gh-serif"
          style={{
            color: 'var(--gh-graphite)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.15,
            marginBottom: '1.75rem',
          }}
        >
          {headline}
        </h1>

        <p
          className="mx-auto max-w-xl"
          style={{
            color: 'var(--gh-graphite)',
            fontSize: '1.0625rem',
            lineHeight: 1.6,
            marginBottom: '2.5rem',
            opacity: 0.85,
          }}
        >
          Melio is a small clinical practice for people who want their genetics,
          family history, and biomarkers held together in one place — and
          reviewed each quarter by a clinician who knows them.
        </p>

        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link
            to={ctaHref}
            className="gh-btn-primary"
            style={{ padding: '14px 28px', fontSize: '0.8125rem' }}
          >
            Begin your enrollment
          </Link>
          <Link to="/membership" className="gh-nav-link" style={{ fontSize: '0.875rem' }}>
            Read about the practice
          </Link>
        </div>
      </div>
    </section>
  );
}
